import { ReactNode, useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { PosSelect, PosSelectProps } from 'src/core/POS/PosSelect';
import { useGetFulfillmentProviderSettings } from 'src/hooks/useGetFulfillmentProviderSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { MapFulfillmentProviderToIcon } from 'src/navigations/Routes/Settings/Views/FulfillmentProviders';
import { TeamMembersIcon } from 'src/svgs/TeamMembersIcon';
import {
  EMPTY_GUID,
  FULFILLMENT_PROVIDER_SELLER_USER_IDS,
} from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

export const SellerAccountEmployeeSelector = ({
  placeholderText,
  unchangedText,
  ...props
}: Omit<PosSelectProps, 'valueOptionsContent'> & {
  showEmail?: boolean;
  allowUnchanged?: boolean;
  unchangedText?: string;
  isFulfillmentSection?: boolean;
}) => {
  const { allActiveUserInfos } = useSellerAccountContext();
  const unchanged = useContent(ContentId.DontChange);

  const hasUserTeams = useUserHasFeature(Feature.UserTeams);

  const teamIds = useMemo(() => {
    if (!hasUserTeams || !allActiveUserInfos) return new Set<string>();
    return new Set(
      Object.entries(allActiveUserInfos)
        .filter(([_, info]) => info.isTeam)
        .map(([id]) => id)
    );
  }, [hasUserTeams, allActiveUserInfos]);

  const { fulfillmentProviderSettings } = useGetFulfillmentProviderSettings();

  const allActiveUserOptions = useMemo(() => {
    if (!allActiveUserInfos) return {};

    const results = {} as Record<string, string>;
    if (props.allowUnchanged) {
      results[EMPTY_GUID] = unchangedText ?? unchanged;
    }

    if (props.isFulfillmentSection) {
      if (fulfillmentProviderSettings?.length) {
        fulfillmentProviderSettings.forEach((provider) => {
          if (provider.isEnabled) {
            results[provider.fulfillmentProviderSellerUserId] =
              provider.fulfillmentProviderName;
          }
        });
      }
    }

    Object.values(allActiveUserInfos).reduce((r, cur) => {
      r[cur.userId] = props.showEmail
        ? `${cur.displayName} (${cur.email})`
        : cur.displayName;
      return r;
    }, results);

    return results;
  }, [
    allActiveUserInfos,
    fulfillmentProviderSettings,
    props.allowUnchanged,
    props.isFulfillmentSection,
    props.showEmail,
    unchangedText,
    unchanged,
  ]);

  // LEFT side — fulfillment provider icons only (unchanged behavior)
  const allActiveValueOptionIcons = useMemo(() => {
    if (!allActiveUserOptions) return {};

    const results = {} as Record<string, ReactNode>;
    for (const [sellerUserId, displayName] of Object.entries(
      allActiveUserOptions
    )) {
      if (FULFILLMENT_PROVIDER_SELLER_USER_IDS.has(sellerUserId)) {
        results[sellerUserId] = MapFulfillmentProviderToIcon(displayName);
      }
    }
    return results;
  }, [allActiveUserOptions]);

  // RIGHT side — team icons only (new)
  const allActiveValueOptionPostfixIcons = useMemo(() => {
    if (!allActiveUserOptions || teamIds.size === 0) return {};

    const results = {} as Record<string, ReactNode>;
    for (const sellerUserId of Object.keys(allActiveUserOptions)) {
      if (teamIds.has(sellerUserId)) {
        results[sellerUserId] = <TeamMembersIcon />;
      }
    }
    return results;
  }, [allActiveUserOptions, teamIds]);

  return (
    <PosSelect
      {...props}
      searchable={
        props.searchable != null
          ? props.searchable
          : Object.values(allActiveUserOptions).length > 10
      }
      sortFn={(sellerIdA, sellerIdB) => {
        if (sellerIdA === EMPTY_GUID) return -1;
        if (sellerIdB === EMPTY_GUID) return 1;

        const isFulfillmentA =
          FULFILLMENT_PROVIDER_SELLER_USER_IDS.has(sellerIdA);
        const isFulfillmentB =
          FULFILLMENT_PROVIDER_SELLER_USER_IDS.has(sellerIdB);
        const isTeamA = teamIds.has(sellerIdA);
        const isTeamB = teamIds.has(sellerIdB);

        // Priority: fulfillment > team > regular
        const rankA = isFulfillmentA ? 0 : isTeamA ? 1 : 2;
        const rankB = isFulfillmentB ? 0 : isTeamB ? 1 : 2;

        if (rankA !== rankB) return rankA - rankB;

        // Within same rank, sort alphabetically
        return (allActiveUserOptions[sellerIdA] ?? '').localeCompare(
          allActiveUserOptions[sellerIdB] ?? ''
        );
      }}
      loading={allActiveUserInfos == null}
      placeholderText={placeholderText ?? ContentId.Everyone}
      valueOptionsContent={allActiveUserOptions}
      valueOptionsIcon={allActiveValueOptionIcons}
      valueOptionsPostfixIcon={allActiveValueOptionPostfixIcons}
      sortBy="key"
    />
  );
};
